import {
  MyCoordinationShipment,
  MyCoordinationSvcGroup,
  MyCoordinationUncalledDriver,
} from "@/types";

export interface UnitTypeGroup {
  /** null = sin tipo de unidad conocido */
  unitTypeName: string | null;
  shipments: MyCoordinationShipment[];
}

export interface SvcShiftGroup {
  svc: { id: number; code: string; name: string };
  total: number;
  /** Rutas sin chofer asignado */
  unassigned: number;
  unitTypes: UnitTypeGroup[];
}

export interface ShiftGroup {
  /** "AM" | "PM" | null (rutas sin turno) */
  shift: string | null;
  total: number;
  svcs: SvcShiftGroup[];
}

const SHIFT_ORDER = ["AM", "PM"];

const shiftRank = (shift: string | null): number => {
  if (shift === null) return SHIFT_ORDER.length + 1;
  const idx = SHIFT_ORDER.indexOf(shift);
  return idx === -1 ? SHIFT_ORDER.length : idx;
};

/**
 * Reagrupa la respuesta de /me/coordinations (grupos por SVC) en la jerarquía
 * que muestra la pantalla: turno → SVC → tipo de unidad → rutas.
 * Conserva el orden por horario que ya trae el backend dentro de cada grupo.
 */
export function groupCoordinations(groups: MyCoordinationSvcGroup[]): ShiftGroup[] {
  const byShift = new Map<string | null, Map<number, SvcShiftGroup>>();

  for (const group of groups) {
    for (const s of group.shipments) {
      const shift = s.shift ?? null;
      let svcsOfShift = byShift.get(shift);
      if (!svcsOfShift) {
        svcsOfShift = new Map();
        byShift.set(shift, svcsOfShift);
      }

      let svcGroup = svcsOfShift.get(group.svc.id);
      if (!svcGroup) {
        svcGroup = { svc: group.svc, total: 0, unassigned: 0, unitTypes: [] };
        svcsOfShift.set(group.svc.id, svcGroup);
      }

      svcGroup.total += 1;
      if (!s.driver) svcGroup.unassigned += 1;

      const unitTypeName = s.unitTypeName ?? null;
      let unitGroup = svcGroup.unitTypes.find((u) => u.unitTypeName === unitTypeName);
      if (!unitGroup) {
        unitGroup = { unitTypeName, shipments: [] };
        svcGroup.unitTypes.push(unitGroup);
      }
      unitGroup.shipments.push(s);
    }
  }

  const result: ShiftGroup[] = [];
  for (const [shift, svcsOfShift] of byShift) {
    const svcs = Array.from(svcsOfShift.values());
    for (const svcGroup of svcs) {
      svcGroup.unitTypes.sort((a, b) => {
        if (a.unitTypeName === null) return 1;
        if (b.unitTypeName === null) return -1;
        return a.unitTypeName.localeCompare(b.unitTypeName, "es");
      });
    }
    svcs.sort((a, b) => a.svc.name.localeCompare(b.svc.name, "es"));
    result.push({
      shift,
      total: svcs.reduce((acc, s) => acc + s.total, 0),
      svcs,
    });
  }

  result.sort((a, b) => shiftRank(a.shift) - shiftRank(b.shift));
  return result;
}

export interface UncalledSection {
  svc: { id: number; code: string; name: string };
  drivers: MyCoordinationUncalledDriver[];
}

/**
 * Secciones de "no citados" por SVC: descarta los SVCs sin choferes pendientes y
 * ordena por nombre de SVC. El orden de choferes (turno y nombre) ya viene del backend.
 * `uncalled` puede faltar en respuestas cacheadas viejas.
 */
export function uncalledSections(groups: MyCoordinationSvcGroup[]): UncalledSection[] {
  return groups
    .filter((g) => (g.uncalled?.length ?? 0) > 0)
    .map((g) => ({ svc: g.svc, drivers: g.uncalled ?? [] }))
    .sort((a, b) => a.svc.name.localeCompare(b.svc.name, "es"));
}
